import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import ModalsComponent from '../stories/atoms/modals'
import { modalSizeOptions } from '../stories/atoms/modals/constants'
import TextInputComponent from '../stories/molecules/text-input'
import {
  buttonVariantOptions,
  buttonSizeOptions
} from '../stories/atoms/button/constants'

import {
  FormContainer,
  ModalBody,
  ModalContentContainer
} from '../../containers/project-menu/styled-component'

import { textInputHintVariantOptions } from '../stories/molecules/text-input/constants'
import StaticBannerComponent from '../stories/molecules/static-banner'
import { staticBannerVariantOptions } from '../stories/molecules/static-banner/constants'
import RecursiveFolderList from '../recursive-folder-list'
import { SpinnerGrey } from '../stories/atoms/icons/special'
import { iconSizeOptions } from '../stories/atoms/icons/icons-sizes'
import { recursiveListTypeOptions } from '../recursive-folder-list/constants'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'

const CreateFolderModal = ({
  handleCreate = () => {},
  handleClose = () => {},
  createFolderError = null,
  createFolderLoading = false,
  isLoading = false,
  defaultFolderId = null,
  folderNameLimitLength = 64,
  folderNameHintMessage = null,
  foldersListType = recursiveListTypeOptions.PROJECT,
  projectId = null,
  planRootFolderId = null
}) => {
  const { formatMessage } = useIntl()
  const [folderName, setFolderName] = useState(null)
  const [folderNameHint, setFolderNameHint] = useState(null)
  const [parentFolderId, setParentFolderId] = useState(defaultFolderId)
  const showCounter = folderName?.length >= folderNameLimitLength * 0.8

  const handleSelectFolderId = (folder) => {
    if (folder && folder.id) {
      return setParentFolderId(folder.id)
    }

    return setParentFolderId(null)
  }

  const handleCheckNameMinimumLength = (e) => {
    const value = e.target.value
    if (value.length > 0 && value.length <= 3) {
      setFolderNameHint(
        formatMessage(
          { id: 'passwordOrientation_minimumLength' },
          { number: 4 }
        )
      )
    } else {
      setFolderNameHint(null)
    }
  }

  const handleChangeTitle = (e) => {
    handleCheckNameMinimumLength(e)
    setFolderName(e.target.value)
  }

  const handleCreateFolder = () => {
    setFolderNameHint(null)
    handleCreate({
      folderName,
      parentFolderId
    })
  }

  const modalButtons = [
    {
      title: formatMessage({ id: 'cancel' }),
      variant: buttonVariantOptions['TERTIARY-HOLLOW'],
      size: buttonSizeOptions.MEDIUM,
      onClick: handleClose
    },
    {
      title: formatMessage({ id: 'create' }),
      variant: buttonVariantOptions.PRIMARY,
      size: buttonSizeOptions.MEDIUM,
      onClick: handleCreateFolder,
      disabled: createFolderLoading || isEmpty(folderName),
      isLoading
    }
  ]

  useEffect(() => {
    if (folderNameHintMessage) {
      setFolderNameHint(folderNameHintMessage)
    }
  }, [folderNameHintMessage])

  return (
    <ModalsComponent
      isOpen={true}
      title={formatMessage({ id: 'projects_newFolder' })}
      size={modalSizeOptions.SMALL}
      rightButtons={modalButtons}
      onClose={handleClose}
      showCloseButton={false}
      autoHeight
    >
      <ModalBody>
        {isLoading ? (
          <SpinnerGrey iconSize={iconSizeOptions['ICON-L']} />
        ) : (
          <>
            {createFolderError && (
              <StaticBannerComponent
                variant={staticBannerVariantOptions.ERROR}
                message={createFolderError}
              />
            )}
            <ModalContentContainer>
              <FormContainer>
                <TextInputComponent
                  defaultValue={folderName}
                  label={formatMessage({ id: 'title' })}
                  maxCharacters={64}
                  showCounter={showCounter}
                  clearButton
                  onChange={handleChangeTitle}
                  hintShow={folderNameHint}
                  hintMessage={folderNameHint}
                  hintVariant={textInputHintVariantOptions.ERROR}
                  isTextarea
                  focusOnMount
                />

                <RecursiveFolderList
                  onSelectItem={handleSelectFolderId}
                  defaultFolderId={defaultFolderId}
                  planRootFolderId={planRootFolderId}
                  listType={foldersListType}
                  projectId={projectId}
                />
              </FormContainer>
            </ModalContentContainer>
          </>
        )}
      </ModalBody>
    </ModalsComponent>
  )
}

CreateFolderModal.propTypes = {
  handleCreate: PropTypes.func,
  handleClose: PropTypes.func,
  createFolderError: PropTypes.string,
  createFolderLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  defaultFolderId: PropTypes.number,
  folderNameLimitLength: PropTypes.number,
  folderNameHintMessage: PropTypes.string,
  foldersListType: PropTypes.oneOf(Object.values(recursiveListTypeOptions)),
  projectId: PropTypes.string,
  planRootFolderId: PropTypes.string
}

export default CreateFolderModal
